import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Users, DollarSign, BarChart3, Calendar, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useClients } from "@/hooks/useClients";
import { PageTour } from "@/components/tour/PageTour";
import { adminDashboardsSteps } from "@/lib/tour-steps";
import {
  ResponsiveContainer, XAxis, YAxis, Tooltip as RechartsTooltip,
  PieChart, Pie, Cell, BarChart, Bar, CartesianGrid, Legend
} from "recharts";

const COLORS = ['hsl(182, 52%, 58%)', 'hsl(45, 93%, 47%)', 'hsl(270, 70%, 60%)', 'hsl(150, 60%, 50%)', 'hsl(200, 70%, 50%)'];

interface ClientRevenue {
  clientId: string;
  clientName: string;
  contactName: string;
  package: 'standard' | 'elite';
  demosDelivered: number;
  billableDemos: number;
  demoRevenue: number;
  revenueShare: number;
  setupFee: number;
  totalRevenue: number;
  momChange: number;
}

export default function AdminDashboards() {
  const { getClientStats, demos, clients } = useClients();
  const stats = getClientStats();
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedMonth, setSelectedMonth] = useState("current");

  // Fetch actual billing data from weekly_billing (authoritative source)
  const [billingByClient, setBillingByClient] = useState<Record<string, {
    demoRevenueCents: number; revenueShareCents: number; totalAmountCents: number;
    demosDelivered: number; billableDemos: number;
  }>>({});

  useEffect(() => {
    const fetchBilling = async () => {
      const { data } = await supabase
        .from('weekly_billing')
        .select('client_id, demo_revenue_cents, revenue_share_cents, total_amount_cents, demos_delivered, billable_demos');
      if (data) {
        const byClient: typeof billingByClient = {};
        for (const row of data) {
          if (!byClient[row.client_id]) {
            byClient[row.client_id] = { demoRevenueCents: 0, revenueShareCents: 0, totalAmountCents: 0, demosDelivered: 0, billableDemos: 0 };
          }
          byClient[row.client_id].demoRevenueCents += row.demo_revenue_cents || 0;
          byClient[row.client_id].revenueShareCents += row.revenue_share_cents || 0;
          byClient[row.client_id].totalAmountCents += row.total_amount_cents || 0;
          byClient[row.client_id].demosDelivered += row.demos_delivered || 0;
          byClient[row.client_id].billableDemos += row.billable_demos || 0;
        }
        setBillingByClient(byClient);
      }
    };
    fetchBilling();
  }, []);

  const packageData = [
    { name: 'Standard', value: stats.standard, percentage: stats.total > 0 ? Math.round(stats.standard / stats.total * 100) : 0 },
    { name: 'Elite', value: stats.elite, percentage: stats.total > 0 ? Math.round(stats.elite / stats.total * 100) : 0 },
  ];

  // Calculate revenue per client — use actual billing data when available, estimates as fallback
  const clientRevenueData: ClientRevenue[] = useMemo(() => {
    return clients.map(client => {
      const billing = billingByClient[client.id];

      // Use actual weekly_billing data when it exists (authoritative)
      if (billing && billing.totalAmountCents > 0) {
        return {
          clientId: client.id,
          clientName: client.name,
          contactName: client.contactName,
          package: client.package,
          demosDelivered: billing.demosDelivered,
          billableDemos: billing.billableDemos,
          demoRevenue: Math.round(billing.demoRevenueCents / 100),
          revenueShare: Math.round(billing.revenueShareCents / 100),
          setupFee: 0,
          totalRevenue: Math.round(billing.totalAmountCents / 100),
          momChange: 0,
        };
      }

      // Fallback: estimate from demos (for clients not yet billed)
      const clientDemos = demos.filter(d => d.clientId === client.id && d.status === 'completed');
      const demosDelivered = clientDemos.length;
      const billableDemos = Math.max(0, demosDelivered - 3);
      const demoPrice = client.package === 'elite' ? 200 : 150;
      const demoRevenue = billableDemos * demoPrice;

      const closedRevenue = clientDemos
        .filter(d => d.outcome === 'closed' && d.dealValue)
        .reduce((sum, d) => sum + (d.dealValue || 0), 0);
      const revenueShare = closedRevenue > 50000 ? (closedRevenue - 50000) * 0.075 : 0;

      const totalRevenue = demoRevenue + revenueShare;

      return {
        clientId: client.id,
        clientName: client.name,
        contactName: client.contactName,
        package: client.package,
        demosDelivered,
        billableDemos,
        demoRevenue,
        revenueShare: Math.round(revenueShare),
        setupFee: 0,
        totalRevenue,
        momChange: 0,
      };
    });
  }, [clients, demos, billingByClient]);

  const revenueTotals = useMemo(() => {
    return clientRevenueData.reduce((acc, r) => ({
      demosDelivered: acc.demosDelivered + r.demosDelivered,
      billableDemos: acc.billableDemos + r.billableDemos,
      demoRevenue: acc.demoRevenue + r.demoRevenue,
      revenueShare: acc.revenueShare + r.revenueShare,
      setupFees: acc.setupFees + r.setupFee,
      totalRevenue: acc.totalRevenue + r.totalRevenue,
    }), { demosDelivered: 0, billableDemos: 0, demoRevenue: 0, revenueShare: 0, setupFees: 0, totalRevenue: 0 });
  }, [clientRevenueData]);

  // Client demo data for charts
  const clientDemoData = useMemo(() => {
    return clients.map(client => {
      const clientDemos = demos.filter(d => d.clientId === client.id);
      const demosBooked = clientDemos.length;
      const demosShowed = clientDemos.filter(d => d.status === 'completed').length;
      const demosClosed = clientDemos.filter(d => d.outcome === 'closed').length;
      const noShows = clientDemos.filter(d => d.status === 'no_show').length;
      const cancelled = clientDemos.filter(d => d.status === 'cancelled').length;
      
      return {
        name: client.name,
        companyName: client.name,
        demosBooked,
        demosShowed,
        demosClosed,
        noShows,
        cancelled,
        showRate: demosBooked > 0 ? Math.round(demosShowed / demosBooked * 100) : 0,
        closeRate: demosShowed > 0 ? Math.round(demosClosed / demosShowed * 100) : 0,
      };
    });
  }, [clients, demos]);

  // Revenue distribution for pie chart
  const revenueDistribution = useMemo(() => {
    return clientRevenueData
      .filter(r => r.totalRevenue > 0)
      .map(r => ({
        name: r.clientName,
        value: r.totalRevenue
      }));
  }, [clientRevenueData]);

  // Monthly trend data derived from actual client revenue
  const monthlyTrendData = useMemo(() => {
    if (clients.length === 0) return [];
    // Use actual current revenue as the data point; historical trends require billing history
    const currentMonth = new Date().toLocaleString('default', { month: 'short' });
    return [{
      month: currentMonth,
      ...Object.fromEntries(
        clientRevenueData.slice(0, 5).map(r => [r.clientName, r.totalRevenue])
      ),
    }];
  }, [clients, clientRevenueData]);

  const totalMRR = clientRevenueData.reduce((sum, r) => sum + r.demoRevenue, 0);
  const avgRevenuePerClient = clients.length > 0 ? Math.round(revenueTotals.totalRevenue / clients.length) : 0;
  const topPerformer = clientRevenueData.reduce((max, r) => r.totalRevenue > (max?.totalRevenue || 0) ? r : max, clientRevenueData[0]);

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={adminDashboardsSteps} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Client Performance Dashboards</h1>
          <p className="text-muted-foreground">Comprehensive view of client metrics and revenue</p>
        </div>
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-[180px]">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Select month" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="current">This Month</SelectItem>
            <SelectItem value="last">Last Month</SelectItem>
            <SelectItem value="2months">2 Months Ago</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-success/20">
              <DollarSign className="w-6 h-6 text-success" />
            </div>
            <div>
              <p className="text-3xl font-bold">${totalMRR.toLocaleString()}</p>
              <div className="flex items-center gap-1">
                <p className="text-sm text-muted-foreground">Total MRR</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Monthly recurring revenue from demo fees across all active clients</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-xs text-muted-foreground">This month</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-secondary/20">
              <Users className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <p className="text-3xl font-bold">${avgRevenuePerClient.toLocaleString()}</p>
              <div className="flex items-center gap-1">
                <p className="text-sm text-muted-foreground">Avg Revenue/Client</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Total portfolio revenue divided by number of active clients</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-xs text-muted-foreground">Per client</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-warning/20">
              <TrendingUp className="w-6 h-6 text-warning" />
            </div>
            <div>
              <p className="text-xl font-bold truncate">{topPerformer?.clientName || 'N/A'}</p>
              <div className="flex items-center gap-1">
                <p className="text-sm text-muted-foreground">Top Performer</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Client generating the highest total revenue this period</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-xs text-success">${topPerformer?.totalRevenue.toLocaleString() || 0}</p>
            </div>
          </VentureCardContent>
        </VentureCard>

        <VentureCard>
          <VentureCardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary/20">
              <BarChart3 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-3xl font-bold">{revenueTotals.demosDelivered}</p>
              <div className="flex items-center gap-1">
                <p className="text-sm text-muted-foreground">Total Demos</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Total completed demos delivered across all clients (all time)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-xs text-muted-foreground">All time</p>
            </div>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="revenue">Revenue by Client</TabsTrigger>
          <TabsTrigger value="demos">Demos by Client</TabsTrigger>
          <TabsTrigger value="charts">Charts & Trends</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Weekly Trend */}
            <VentureCard>
              <VentureCardHeader>
                <VentureCardTitle>Demo Delivery Trend</VentureCardTitle>
              </VentureCardHeader>
              <VentureCardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={clientDemoData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <RechartsTooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                      <Bar dataKey="demosBooked" fill="hsl(var(--secondary))" name="Booked" />
                      <Bar dataKey="demosShowed" fill="hsl(var(--success))" name="Showed" />
                      <Bar dataKey="demosClosed" fill="hsl(var(--primary))" name="Closed" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </VentureCardContent>
            </VentureCard>

            {/* Package Distribution */}
            <VentureCard>
              <VentureCardHeader>
                <VentureCardTitle>Package Distribution</VentureCardTitle>
              </VentureCardHeader>
              <VentureCardContent>
                <div className="h-64 flex items-center justify-center">
                  {stats.total > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={packageData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                          {packageData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-muted-foreground">No client data yet</p>
                  )}
                </div>
                <div className="flex justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[0] }} />
                    <span className="text-sm">Standard: {packageData[0].value} ({packageData[0].percentage}%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[1] }} />
                    <span className="text-sm">Elite: {packageData[1].value} ({packageData[1].percentage}%)</span>
                  </div>
                </div>
              </VentureCardContent>
            </VentureCard>
          </div>
        </TabsContent>

        {/* Revenue Tab */}
        <TabsContent value="revenue" className="mt-6">
          <VentureCard>
            <VentureCardHeader>
              <VentureCardTitle>Monthly Revenue by Client</VentureCardTitle>
            </VentureCardHeader>
            <VentureCardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Package</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Demos</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Billable</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Demo Rev</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Rev Share</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Setup Fee</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientRevenueData.map((r) => (
                      <tr key={r.clientId} className="border-b border-border hover:bg-surface transition-colors">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{r.clientName}</p>
                            <p className="text-xs text-muted-foreground">{r.contactName}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <BadgeVenture variant={r.package === 'elite' ? 'warning' : 'info'}>
                            {r.package}
                          </BadgeVenture>
                        </td>
                        <td className="py-3 px-4 text-center">{r.demosDelivered}</td>
                        <td className="py-3 px-4 text-center text-muted-foreground">{r.billableDemos} <span className="text-xs">(first 3 free)</span></td>
                        <td className="py-3 px-4 text-right">${r.demoRevenue.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right">
                          {r.revenueShare > 0 ? (
                            <span className="text-success">${r.revenueShare.toLocaleString()}</span>
                          ) : (
                            <span className="text-muted-foreground">N/A</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {r.setupFee > 0 ? (
                            <span className="text-secondary">${r.setupFee.toLocaleString()}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right font-bold">${r.totalRevenue.toLocaleString()}</td>
                      </tr>
                    ))}
                    <tr className="bg-surface font-bold">
                      <td className="py-3 px-4" colSpan={2}>MONTHLY TOTAL</td>
                      <td className="py-3 px-4 text-center">{revenueTotals.demosDelivered}</td>
                      <td className="py-3 px-4 text-center">{revenueTotals.billableDemos}</td>
                      <td className="py-3 px-4 text-right">${revenueTotals.demoRevenue.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right">${revenueTotals.revenueShare.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right">${revenueTotals.setupFees.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right">${revenueTotals.totalRevenue.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </VentureCardContent>
          </VentureCard>
        </TabsContent>

        {/* Demos Tab */}
        <TabsContent value="demos" className="mt-6 space-y-6">
          <VentureCard>
            <VentureCardHeader>
              <VentureCardTitle>Demo Delivery by Client</VentureCardTitle>
            </VentureCardHeader>
            <VentureCardContent>
              <div className="h-[400px] mb-6">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clientDemoData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="companyName" stroke="hsl(var(--muted-foreground))" angle={-45} textAnchor="end" height={100} />
                    <YAxis stroke="hsl(var(--muted-foreground))" />
                    <RechartsTooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                    <Legend />
                    <Bar dataKey="demosBooked" fill="hsl(217, 91%, 60%)" name="Booked" />
                    <Bar dataKey="demosShowed" fill="hsl(142, 71%, 45%)" name="Showed" />
                    <Bar dataKey="demosClosed" fill="hsl(270, 70%, 60%)" name="Closed" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Booked</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Showed</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Show Rate</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Closed</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Close Rate</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">No-Shows</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Cancelled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientDemoData.map((d, i) => (
                      <tr key={i} className="border-b border-border hover:bg-surface transition-colors">
                        <td className="py-3 px-4 font-medium">{d.companyName}</td>
                        <td className="py-3 px-4 text-center">{d.demosBooked}</td>
                        <td className="py-3 px-4 text-center">{d.demosShowed}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={d.showRate >= 75 ? 'text-success' : d.showRate >= 50 ? 'text-warning' : 'text-destructive'}>
                            {d.showRate}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">{d.demosClosed}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={d.closeRate >= 30 ? 'text-success' : d.closeRate >= 20 ? 'text-warning' : 'text-destructive'}>
                            {d.closeRate}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-muted-foreground">{d.noShows}</td>
                        <td className="py-3 px-4 text-center text-muted-foreground">{d.cancelled}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </VentureCardContent>
          </VentureCard>
        </TabsContent>

        {/* Charts Tab */}
        <TabsContent value="charts" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Distribution */}
            <VentureCard>
              <VentureCardHeader>
                <VentureCardTitle>Revenue Distribution</VentureCardTitle>
              </VentureCardHeader>
              <VentureCardContent>
                <div className="h-64">
                  {revenueDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={revenueDistribution} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                          {revenueDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(value: number) => `$${value.toLocaleString()}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <p className="text-muted-foreground">No revenue data</p>
                    </div>
                  )}
                </div>
              </VentureCardContent>
            </VentureCard>

            {/* Package Distribution */}
            <VentureCard>
              <VentureCardHeader>
                <VentureCardTitle>Package Distribution</VentureCardTitle>
              </VentureCardHeader>
              <VentureCardContent>
                <div className="h-64 flex items-center justify-center">
                  {stats.total > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={packageData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value">
                          {packageData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-muted-foreground">No client data</p>
                  )}
                </div>
                <div className="flex justify-center gap-6 mt-4">
                  <div>
                    <span className="text-muted-foreground">Standard: </span>
                    <span className="font-medium">{packageData[0].value} ({packageData[0].percentage}%)</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Elite: </span>
                    <span className="font-medium">{packageData[1].value} ({packageData[1].percentage}%)</span>
                  </div>
                </div>
              </VentureCardContent>
            </VentureCard>

            {/* Revenue Trend */}
            <VentureCard className="lg:col-span-2">
              <VentureCardHeader>
                <VentureCardTitle>Revenue by Client (Current)</VentureCardTitle>
              </VentureCardHeader>
              <VentureCardContent>
                <div className="h-[300px]">
                  {monthlyTrendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={clientRevenueData.filter(r => r.totalRevenue > 0)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="clientName" stroke="hsl(var(--muted-foreground))" angle={-45} textAnchor="end" height={80} />
                        <YAxis stroke="hsl(var(--muted-foreground))" />
                        <RechartsTooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(value: number) => `$${Math.round(value).toLocaleString()}`} />
                        <Bar dataKey="totalRevenue" fill="hsl(var(--secondary))" name="Total Revenue" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <p className="text-muted-foreground">No revenue data yet. Historical trends will appear as billing data accumulates.</p>
                    </div>
                  )}
                </div>
              </VentureCardContent>
            </VentureCard>
          </div>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
